import React, { useMemo } from 'react';
import classNames from 'classnames';
import './CustomSVG.scss';

// Shape tags we know how to re-render. Anything outside this list (text, use, gradients, masks, ...)
// is silently dropped, same as before - this just widens the net past <path> alone.
const SHAPE_TAGS = ['path', 'circle', 'rect', 'ellipse', 'polygon', 'polyline', 'line'];
// class/fill are resolved separately below; id is dropped so the same icon can render more than
// once on a page without duplicate DOM ids.
const OMITTED_ATTRS = ['class', 'fill', 'id'];
// Source SVGs use kebab-case attribute names (stroke-width, stroke-miterlimit, ...), but React's
// SVG attribute allowlist expects the camelCase form for these and warns otherwise. data-*/aria-*
// are the exception - React accepts those verbatim, so leave them alone.
const toReactAttrName = (name) =>
    name.startsWith('data-') || name.startsWith('aria-')
        ? name
        : name.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());

/**
 * Renders SVG, extracts raw content that can be customized through props
 * If preservedColors is passed, the original colors of the SVG will be preserved,
 * otherwise they will be overridden by CSS
 * warning/error/grey override every shape's color (including preserved ones) with a single themed fill
 */
export const CustomSVG = ({
    svg,
    className,
    preserveColors,
    appIconSmall,
    appIconMedium,
    appIconLarge,
    cloudIconSmall,
    cloudIconMedium,
    cloudIconLarge,
    navIconDefault,
    error,
    warning,
    grey,
    style,
    ...props
}) => {
    let fill;
    if (error) {
        fill = 'var(--color-error)';
    } else if (warning) {
        fill = 'var(--color-warning)';
    } else if (grey) {
        fill = 'var(--color-text-subtle)';
    }
    const resolvedPreserveColors = fill ? false : preserveColors;

    const parsedSVG = useMemo(() => {
        if (!svg) return null;

        const parser = new DOMParser();
        const doc = parser.parseFromString(svg, 'image/svg+xml');
        const svgElement = doc.querySelector('svg');

        if (!svgElement) {
            return null;
        }

        // Shapes inside <defs> (e.g. a <rect> used only to define a <clipPath>) are templates,
        // not visible content - rendering them directly draws a stray box over the icon.
        const shapes = Array.from(svgElement.querySelectorAll(SHAPE_TAGS.join(',')))
            .filter((shape) => !shape.closest('defs'))
            .map((shape) => {
                const shapeClassName = shape.getAttribute('class');
                // Carry through whatever geometry attributes this shape has (d, cx/cy/r, points, ...)
                // without needing to special-case each tag type.
                const attrs = {};
                for (const name of shape.getAttributeNames()) {
                    if (!OMITTED_ATTRS.includes(name)) {
                        attrs[toReactAttrName(name)] = shape.getAttribute(name);
                    }
                }
                return {
                    tag: shape.tagName,
                    className: shapeClassName,
                    fill: shape.getAttribute('fill'),
                    attrs,
                };
            });

        // Preserve defs (gradients, masks, clip paths, ...) that shapes can still reference
        // via e.g. fill="url(#grad1)".
        const defsElement = svgElement.querySelector('defs');
        const defsHTML = defsElement ? defsElement.innerHTML || null : null;

        return {
            viewBox: svgElement.getAttribute('viewBox'),
            defsHTML,
            shapes,
        };
    }, [svg]);

    const svgClassName = classNames(
        'rm-svg',
        {
            'app-icon-small': appIconSmall,
            'app-icon-medium': appIconMedium,
            'app-icon-large': appIconLarge,
            'cloud-icon-small': cloudIconSmall,
            'cloud-icon-medium': cloudIconMedium,
            'cloud-icon-large': cloudIconLarge,
            'nav-icon-default': navIconDefault,
        },
        className,
    );
    const shapeElements = parsedSVG?.shapes?.map((shape, index) =>
        React.createElement(shape.tag, {
            key: index,
            // shape.className (e.g. Illustrator's "st0") only means anything alongside the matching
            // <defs><style> we render below - and that <style> isn't scoped to this <svg>, so it
            // leaks globally. Dropping the class whenever we drop the defs keeps a color-overridden
            // icon from being repainted by some *other* unrelated instance of the same icon on the page.
            className: resolvedPreserveColors ? classNames(shape.className) : undefined,
            // Without preserveColors, fill is left undefined so the CSS on .rm-svg
            // (fill: var(--icon-default)) controls the color instead of the source SVG.
            fill: resolvedPreserveColors ? shape.fill : undefined,
            ...shape.attrs,
        }),
    );

    return (
        <svg viewBox={parsedSVG?.viewBox} className={svgClassName} style={fill ? { fill, ...style } : style} {...props}>
            {resolvedPreserveColors && parsedSVG?.defsHTML && (
                <defs dangerouslySetInnerHTML={{ __html: parsedSVG.defsHTML }} />
            )}
            {shapeElements}
        </svg>
    );
};
